import { useEffect, useState } from "react";
import { graphql } from "~/graphql/codegen";
import { useSubscription } from "@apollo/client/react";
import type {
  ResourceAnnotationsFragment,
  ResourceCommentFragment,
} from "~/graphql/codegen/graphql";
import { IHtmlHighlight, IPdfHighlight } from "~/typings/annotate/annotation";
import { mapHighlightGQLToIHighlight } from "../highlight-util";

const HIGHLIGHT_ADDED_SUBSCRIPTION = graphql(`
  subscription HighlightAdded($articleID: ID!) {
    highlightAdded(articleID: $articleID) {
      ...AnnotationHighlight
    }
  }
`);

const HIGHLIGHT_UPDATED_SUBSCRIPTION = graphql(`
  subscription HighlightUpdated($articleID: ID!) {
    highlightUpdated(articleID: $articleID) {
      ...AnnotationHighlight
    }
  }
`);

const HIGHLIGHT_REMOVED_SUBSCRIPTION = graphql(`
  subscription HighlightRemoved($articleID: ID!) {
    highlightRemoved(articleID: $articleID) {
      id
    }
  }
`);

const COMMENT_ADDED_SUBSCRIPTION = graphql(`
  subscription CommentAdded($articleID: ID!) {
    commentAdded(articleID: $articleID) {
      ...ResourceComment
    }
  }
`);

const COMMENT_REMOVED_SUBSCRIPTION = graphql(`
  subscription CommentRemoved($articleID: ID!) {
    commentRemoved(articleID: $articleID) {
      id
    }
  }
`);

export default function useAnnotationManager(
  articleID?: string,
  annotations?: ResourceAnnotationsFragment,
): [ResourceCommentFragment[], (IHtmlHighlight | IPdfHighlight)[]] {
  const [highlights, setHighlights] = useState<
    (IHtmlHighlight | IPdfHighlight)[]
  >(() => {
    return (annotations?.highlights.map((h) =>
      h.__typename === "HTMLHighlight"
        ? (mapHighlightGQLToIHighlight(h) as IHtmlHighlight)
        : (mapHighlightGQLToIHighlight(h) as IPdfHighlight),
    ) || []) as (IHtmlHighlight | IPdfHighlight)[];
  });

  const [resourceComments, setResourceComments] = useState<
    ResourceCommentFragment[]
  >(annotations?.comments || []);

  useSubscription(HIGHLIGHT_ADDED_SUBSCRIPTION, {
    variables: {
      articleID: articleID || "",
    },
    skip: !articleID,
    onData: (res) => {
      const h = res.data.data?.highlightAdded;
      if (!h) {
        console.error("highlightAdded:", res.data.error);
        return;
      }
      setHighlights((highlights) => {
        if (!highlights.some((x) => x.id == res.data.data?.highlightAdded.id)) {
          return [
            ...highlights,
            h.__typename === "HTMLHighlight"
              ? (mapHighlightGQLToIHighlight(h) as IHtmlHighlight)
              : (mapHighlightGQLToIHighlight(h) as IPdfHighlight),
          ] as (IHtmlHighlight | IPdfHighlight)[];
        }
        return highlights;
      });
    },
  });

  useSubscription(HIGHLIGHT_UPDATED_SUBSCRIPTION, {
    variables: {
      articleID: articleID || "",
    },
    skip: !articleID,
    onData: (res) => {
      const h = res.data.data?.highlightUpdated;
      if (!h) {
        console.error("highlightUpdated:", res.data.error);
        return;
      }
      setHighlights((highlights) => {
        return [
          ...highlights.filter((x) => x.id !== h.id),
          h.__typename === "HTMLHighlight"
            ? (mapHighlightGQLToIHighlight(h) as IHtmlHighlight)
            : (mapHighlightGQLToIHighlight(h) as IPdfHighlight),
        ] as (IHtmlHighlight | IPdfHighlight)[];
      });
    },
  });

  useSubscription(HIGHLIGHT_REMOVED_SUBSCRIPTION, {
    variables: {
      articleID: articleID || "",
    },
    skip: !articleID,
    onData: (res) => {
      const h = res.data.data?.highlightRemoved;
      if (!h) {
        console.error("highlightRemoved:", res.data.error);
        return;
      }
      setHighlights((highlights) => {
        return [...highlights.filter((x) => x.id !== h.id)] as (
          | IHtmlHighlight
          | IPdfHighlight
        )[];
      });
    },
  });

  useSubscription(COMMENT_ADDED_SUBSCRIPTION, {
    variables: {
      articleID: articleID || "",
    },
    skip: !articleID,
    onData: (res) => {
      const c = res.data.data?.commentAdded;
      if (!c) {
        console.error("commentAdded:", res.data.error);
        return;
      }
      setResourceComments((comments) => {
        return [...comments, c];
      });
    },
  });

  useSubscription(COMMENT_REMOVED_SUBSCRIPTION, {
    variables: {
      articleID: articleID || "",
    },
    skip: !articleID,
    onData: (res) => {
      const c = res.data.data?.commentRemoved;
      if (!c) {
        console.error("commentAdded:", res.data.error);
        return;
      }
      setResourceComments((comments) => {
        return comments.filter((x) => x.id !== c.id);
      });
    },
  });

  useEffect(() => {
    if (annotations?.highlights) {
      setHighlights(
        (annotations?.highlights.map((h) =>
          h.__typename === "HTMLHighlight"
            ? (mapHighlightGQLToIHighlight(h) as IHtmlHighlight)
            : (mapHighlightGQLToIHighlight(h) as IPdfHighlight),
        ) || []) as IHtmlHighlight[] | IPdfHighlight[],
      );
    }
    if (annotations?.comments) {
      setResourceComments(annotations?.comments || []);
    }
  }, [annotations]);
  return [resourceComments, highlights];
}
